import { createTheme, type Theme, type SxProps } from '@mui/material/styles';

// Augment the palette to include `surface`
declare module '@mui/material/styles' {
  interface Palette {
    surface: { main: string };
  }
  interface PaletteOptions {
    surface?: { main: string };
  }
}

export type ThemeMode = 'light' | 'dark' | 'system';

/** Contained button styled with paper background — secondary actions beside primary CTAs. */
export const whiteButtonSx: SxProps<Theme> = {
  bgcolor: 'background.paper',
  color: 'text.primary',
  boxShadow: 'none',
  border: '1px solid',
  borderColor: 'divider',
  '&:hover': {
    bgcolor: 'action.hover',
    boxShadow: 'none',
  },
};

export function buildTheme(mode: 'light' | 'dark'): Theme {
  const dark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: dark ? '#7C9EFF' : '#4A72E8',
        light: dark ? '#A8C0FF' : '#7C9EFF',
        dark: dark ? '#4A72E8' : '#2850C8',
        contrastText: dark ? '#0D0E1A' : '#ffffff',
      },
      secondary: {
        main: dark ? '#A78BFA' : '#7C3AED',
        light: dark ? '#C4B5FD' : '#A78BFA',
        dark: dark ? '#7C3AED' : '#5B21B6',
        contrastText: dark ? '#0D0E1A' : '#ffffff',
      },
      background: {
        default: dark ? '#0D0E1A' : '#F4F5FA',
        paper: dark ? '#161829' : '#FFFFFF',
      },
      surface: {
        main: dark ? '#1E2035' : '#ECEEF8',
      },
      divider: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)',
      text: {
        primary: dark ? '#E8EAF6' : '#1A1D2E',
        secondary: dark ? '#9399B8' : '#5C6080',
      },
      success: { main: dark ? '#4ADE80' : '#16A34A' },
      warning: { main: dark ? '#FB923C' : '#EA580C' },
      error: { main: dark ? '#F87171' : '#DC2626' },
      info: { main: dark ? '#38BDF8' : '#0284C7' },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: dark ? '#161829' : '#FFFFFF',
            borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)'}`,
            color: dark ? '#E8EAF6' : '#1A1D2E',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: dark ? '#161829' : '#FFFFFF',
            borderRight: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)'}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: dark ? '#1E2035' : '#FFFFFF',
            border: `1px solid ${dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'}`,
            transition: 'border-color 0.2s, box-shadow 0.2s',
            '&:hover': {
              borderColor: dark ? 'rgba(124,158,255,0.35)' : 'rgba(74,114,232,0.35)',
              boxShadow: dark
                ? '0 4px 24px rgba(124,158,255,0.10)'
                : '0 4px 24px rgba(74,114,232,0.10)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.20)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: dark ? 'rgba(124,158,255,0.5)' : 'rgba(74,114,232,0.5)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 6 } },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: dark ? '#1E2035' : '#F4F5FA',
            border: `1px solid ${dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'}`,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } },
      },
    },
  });
}

export const darkTheme = buildTheme('dark');
export const lightTheme = buildTheme('light');
